import React, { useEffect, useState } from 'react';
import { YStack, View, SizableText, Button, XStack, H3, H4, Card, Separator, Select, Adapt, Sheet, Spinner, Input, TextArea } from 'tamagui';
import * as Animatable from 'react-native-animatable';
import { Dimensions, Modal, Pressable, ScrollView } from 'react-native';
import apis from '@api/api';
import { useQuery } from '@tanstack/react-query';
import LottieView from 'lottie-react-native';
import toTitleCase from '@hooks/toTitleCase';
import { SET_AVAILABLE_WHATSAPP_NUMBERS } from '@reduxLocal/actions/types';
import { useDispatch } from 'react-redux';
import { MaterialIcons, MaterialCommunityIcons } from '@expo/vector-icons';
import useGlobalUser from '@hooks/getGlobalUser';
import ErrorBoundary from '@utils/ErrorBoundary';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

const WhatsappOptions = () => {
  const theme = themeSelector();
  const dispatch = useDispatch();
  const selectedId = useGlobalUser();
  const screenWidth = Dimensions.get('window').width;
  const isTablet = screenWidth > 768;
  const [selectedConversationModel, setSelectedConversationModel] = useState('new_whatsapp_model');
  const [isUpdatingModel, setIsUpdatingModel] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [placeholderValues, setPlaceholderValues] = useState<Record<string, string>>({});
  const [testPhoneNumber, setTestPhoneNumber] = useState('+447738672281');
  const [isSendingTest, setIsSendingTest] = useState(false);

  // WhatsApp Profile Query
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['whatsappProfile'],
    queryFn: async () => await apis.getWhatsAppProfile(),
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });

  // WhatsApp Templates Query
  const { data: templatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ['whatsappTemplates'],
    queryFn: async () => await apis.getWhatsappTemplates(),
    enabled: !!data?.id, // Only fetch if we have WhatsApp profile data
  });

  // Customer Models Query (for conversation model dropdown)
  const { data: customerModels, isLoading: customerModelsLoading } = useQuery({
    queryKey: ['customerModels', selectedId],
    queryFn: async () => {
      const data = await apis.getDataFolders({ userId: selectedId, dataFilter: 'customers' });
      return data || [];
    },
    enabled: !!selectedId,
  });


  // Extract placeholders from template components
  const extractPlaceholders = (template: any) => {
    const placeholders: Array<{ name: string; type: string; componentType: string }> = [];
    
    if (!template?.components) return placeholders;
    
    template.components.forEach((component: any) => {
      if (component.type === 'HEADER' || component.type === 'BODY' || component.type === 'FOOTER') {
        const text = component.text || component.format || '';
        // Extract placeholders like {{1}}, {{2}}, etc. or named placeholders
        const matches = text.match(/\{\{(\d+|[a-zA-Z_][a-zA-Z0-9_]*)\}\}/g);
        if (matches) {
          matches.forEach((match: string) => {
            const placeholderName = match.replace(/\{\{|\}\}/g, '');
            if (!placeholders.find(p => p.name === placeholderName)) {
              placeholders.push({
                name: placeholderName,
                type: 'text',
                componentType: component.type
              });
            }
          });
        }
      }
    });
    
    return placeholders;
  };

  // Get approved templates for selection
  const approvedTemplates = React.useMemo(() => {
    if (!templatesData?.data) return [];
    return templatesData.data.filter((t: any) => t.status === 'APPROVED');
  }, [templatesData]);

  const handleTest = () => {
    setShowTestModal(true);
    setSelectedTemplate(null);
    setPlaceholderValues({});
  };

  const handleTemplateSelect = (template: any) => {
    setSelectedTemplate(template);
    const placeholders = extractPlaceholders(template);
    const initialValues: Record<string, string> = {};
    placeholders.forEach((p) => {
      initialValues[p.name] = '';
    });
    setPlaceholderValues(initialValues);
  };

  const handleSendTestMessage = async () => {
    if (!selectedTemplate) {
      // Fallback to old test method if no template selected
      try {
        setIsSendingTest(true);
        const response = await apis.testWhatsApp();
        console.log(response);
        setShowTestModal(false);
      } catch (error) {
        console.error(error);
      } finally {
        setIsSendingTest(false);
      }
      return;
    }

    if (!data?.phoneNumbers?.[0]?.id) {
      console.error('No phone number available');
      return;
    }

    try {
      setIsSendingTest(true);
      
      // Build components array from placeholder values (WhatsApp API format)
      const placeholders = extractPlaceholders(selectedTemplate);
      
      // Group placeholders by component type and maintain order
      const components: any[] = [];
      
      // Process each component in the template
      selectedTemplate.components?.forEach((component: any) => {
        if (component.type === 'HEADER' || component.type === 'BODY' || component.type === 'FOOTER') {
          const componentParams: any[] = [];
          const text = component.text || component.format || '';
          
          // Extract placeholder indices/names from this component
          const matches = text.match(/\{\{(\d+|[a-zA-Z_][a-zA-Z0-9_]*)\}\}/g);
          if (matches && matches.length > 0) {
            matches.forEach((match: string) => {
              const placeholderName = match.replace(/\{\{|\}\}/g, '');
              const value = placeholderValues[placeholderName] || '';
              componentParams.push({
                type: 'text',
                text: value
              });
            });
          }
          
          // Only add component if it has parameters or is required
          if (componentParams.length > 0 || component.type === 'FOOTER') {
            components.push({
              type: component.type.toLowerCase(),
              parameters: componentParams
            });
          }
        }
      });
      
      // Send template message
      await apis.sendTemplateMessage({
        to: testPhoneNumber,
        templateName: selectedTemplate.name,
        templateLanguage: selectedTemplate.language || 'en',
        parameters: components, // Send components array as parameters
        businessPhoneNumberId: data.phoneNumbers[0].id
      });
      
      setShowTestModal(false);
      setSelectedTemplate(null);
      setPlaceholderValues({});
    } catch (error) {
      console.error('Error sending test message:', error);
    } finally {
      setIsSendingTest(false);
    }
  };

  const handleConversationModelChange = async (newModelId: string) => {
    try {
      setIsUpdatingModel(true);
      await apis.updateWhatsAppConversationModelSettings(newModelId);
      setSelectedConversationModel(newModelId);
      console.log('Conversation model updated successfully:', newModelId);
    } catch (error) {
      console.error('Failed to update conversation model:', error);
      // Optionally show error message to user
    } finally {
      setIsUpdatingModel(false);
    }
  };

  useEffect(() => {
    if (data?.phoneNumbers?.length > 0) {
      // Collect all identifiers for each phone number:
      // - WABA ID (from account or phone number)
      // - Phone number ID
      // - Display phone number
      const allIdentifiers: string[] = [];
      
      // Also collect WABA IDs from accounts if available
      if (data?.accounts && Array.isArray(data.accounts)) {
        data.accounts.forEach((account: any) => {
          if (account?.details?.id) {
            allIdentifiers.push(account.details.id);
          }
        });
      }
      
      // Add WABA ID from root if available
      if (data?.id || data?.businessAccountId) {
        allIdentifiers.push(data.id || data.businessAccountId);
      }
      
      // Add all identifiers from phone numbers
      data.phoneNumbers.forEach((num: any) => {
        // Add phone number ID
        if (num.id) {
          allIdentifiers.push(num.id);
        }
        // Add display phone number
        if (num.display_phone_number) {
          allIdentifiers.push(num.display_phone_number);
        }
        // Add WABA ID if present in phone number object
        if (num.wabaId) {
          allIdentifiers.push(num.wabaId);
        }
      });
      
      // Remove duplicates
      const uniqueIdentifiers = [...new Set(allIdentifiers.filter(Boolean))];
      
      console.log('📱 Storing WhatsApp identifiers for conversation queries:', {
        totalIdentifiers: uniqueIdentifiers.length,
        identifiers: uniqueIdentifiers,
        phoneNumbersCount: data.phoneNumbers.length
      });
      
      dispatch({ type: SET_AVAILABLE_WHATSAPP_NUMBERS, payload: uniqueIdentifiers });
    }
  }, [data]);

  // Set initial conversation model from user's WhatsApp profile
  useEffect(() => {
    if (data?.conversationModelId) {
      setSelectedConversationModel(data.conversationModelId);
    }
  }, [data]);

  // Calculate template statistics
  const templateStats = React.useMemo(() => {
    if (!templatesData?.data) return { total: 0, approved: 0, pending: 0, rejected: 0 };

    const templates = templatesData.data;
    return {
      total: templates.length,
      approved: templates.filter((t: any) => t.status === 'APPROVED').length,
      pending: templates.filter((t: any) => t.status === 'PENDING').length,
      rejected: templates.filter((t: any) => t.status === 'REJECTED').length,
    };
  }, [templatesData]);

  // Prepare conversation model options
  const conversationModelOptions = React.useMemo(() => {
    const options = [
      { value: 'new_whatsapp_model', label: 'New WhatsApp Model', description: 'Create a new conversation model' }
    ];

    if (customerModels?.length > 0) {
      customerModels.forEach((model: any) => {
        options.push({
          value: model._id,
          label: model.dataTitle || model.name || 'Untitled Model',
          description: `${model.count || 0} customers • ${model.dataTags?.join(', ') || 'No tags'}`
        });
      });
    }

    return options;
  }, [customerModels]);

  return (
    <ErrorBoundary>
      <YStack gap="$4">
        {/* Error State */}
        {error && (
          <Animatable.View animation="fadeInUp" duration={600}>
            <Card
              backgroundColor={theme ? "#fef2f2" : "#450a0a"}
              borderColor={theme ? "#fecaca" : "#7f1d1d"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <YStack alignItems="center" gap="$4">
                <View style={{
                  backgroundColor: theme ? "#fee2e2" : "#7f1d1d",
                  borderRadius: 50,
                  padding: 16
                }}>
                  <MaterialIcons name="error-outline" size={48} color="#ef4444" />
                </View>
                <H3 color="#ef4444" fontWeight="600" textAlign="center">
                  Failed to load WhatsApp Business
                </H3>
                <SizableText
                  size="$4"
                  color={theme ? "#64748b" : "#94a3b8"}
                  textAlign="center"
                >
                  Please try reconnecting your WhatsApp Business account, or try removing and re-adding the integration.  
                </SizableText>
                <Button
                  backgroundColor="#ef4444"
                  color="white"
                  onPress={() => refetch()}
                  borderRadius={12}
                  paddingHorizontal="$5"
                  paddingVertical="$3"
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="refresh" size={20} color="white" />
                    <SizableText color="white" fontWeight="600">
                      Retry Connection
                    </SizableText>
                  </XStack>
                </Button>
              </YStack>
            </Card>
          </Animatable.View>
        )}

        {/* Loading State */}
        {isLoading && (
          <Animatable.View animation="fadeIn" duration={600}>
            <Card
              backgroundColor={theme ? "#f8fafc" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
              borderRadius={16}
              padding="$6"
              elevate
            >
              <YStack alignItems="center" gap="$4">
                <Spinner size="large" color="#25D366" />
                <H4 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">
                  Loading WhatsApp Business...
                </H4>
              </YStack>
            </Card>
          </Animatable.View>
        )}

        {/* Profile Data */}
        {data && (
          <>
            {/* Header Section */}
            <Animatable.View animation="fadeInUp" duration={600} delay={100}>
              <Card
                backgroundColor={theme ? "#ffffff" : "#1e293b"}
                borderColor={theme ? "#e2e8f0" : "#334155"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                <XStack alignItems="center" gap="$4" marginBottom="$3">
                  <View style={{
                    width: 60,
                    height: 60,
                    borderRadius: 30,
                    backgroundColor: '#25D366',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <MaterialCommunityIcons name="whatsapp" size={32} color="white" />
                  </View>

                  <YStack flex={1}>
                    <H4
                      color={theme ? "#1e293b" : "#f1f5f9"}
                      fontWeight="700"
                      numberOfLines={1}
                    >
                      {toTitleCase(data?.name || "WhatsApp Business")}
                    </H4>
                    <SizableText
                      size="$3"
                      color={theme ? "#64748b" : "#94a3b8"}
                      numberOfLines={1}
                    >
                      ID: {data?.id}
                    </SizableText>
                  </YStack>
                </XStack>

                {/* Template Statistics */}
                <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginVertical="$4" />

                <YStack gap="$3">
                  <SizableText
                    size="$5"
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="600"
                  >
                    Template Statistics
                  </SizableText>
                  <SizableText
                    size="$3"
                    color={theme ? "#64748b" : "#94a3b8"}
                  >
                    Overview of your WhatsApp message templates
                  </SizableText>

                  {templatesLoading ? (
                    <YStack alignItems="center" padding="$4">
                      <Spinner size="small" color="#25D366" />
                    </YStack>
                  ) : (
                    <XStack gap="$3" flexWrap="wrap" justifyContent="space-between">
                      <StatBadge label="Total" value={templateStats.total} color="#10b981" theme={theme} />
                      <StatBadge label="Approved" value={templateStats.approved} color="#059669" theme={theme} />
                      <StatBadge label="Pending" value={templateStats.pending} color="#f59e0b" theme={theme} />
                      <StatBadge label="Rejected" value={templateStats.rejected} color="#ef4444" theme={theme} />
                    </XStack>
                  )}
                </YStack>

                {/* Conversation Model Selection */}
                <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginVertical="$4" />

                <YStack gap="$3">
                  <SizableText
                    size="$5"
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="600"
                  >
                    Conversation Model
                  </SizableText>
                  <SizableText
                    size="$3"
                    color={theme ? "#64748b" : "#94a3b8"}
                  >
                    {isUpdatingModel ? "Updating conversation model..." : "Select the model for WhatsApp conversations"}
                  </SizableText>

                  <XStack alignItems="center" gap="$2">
                    <Select
                      value={selectedConversationModel}
                      onValueChange={handleConversationModelChange}
                      disablePreventBodyScroll
                    >
                      <Select.Trigger
                        flex={1}
                        backgroundColor={theme ? "#f8fafc" : "#334155"}
                        borderColor={theme ? "#cbd5e1" : "#475569"}
                        iconAfter={<MaterialIcons name="keyboard-arrow-down" size={20} />}
                      >
                        <Select.Value placeholder="Select model..." />
                      </Select.Trigger>

                      <Adapt>
                        <Sheet modal dismissOnSnapToBottom>
                          <Sheet.Frame>
                            <Sheet.ScrollView>
                              <Adapt.Contents />
                            </Sheet.ScrollView>
                          </Sheet.Frame>
                          <Sheet.Overlay
                            animation="lazy"
                            enterStyle={{ opacity: 0 }}
                            exitStyle={{ opacity: 0 }}
                          />
                        </Sheet>
                      </Adapt>

                      <Select.Content zIndex={200000}>
                        <Select.ScrollUpButton
                          alignItems="center"
                          justifyContent="center"
                          position="relative"
                          width="100%"
                          height="$3"
                        >
                          <YStack zIndex={10}>
                            <MaterialIcons name="keyboard-arrow-up" size={20} />
                          </YStack>
                        </Select.ScrollUpButton>

                        <Select.Viewport minHeight={200}>
                          <Select.Group>
                            <Select.Label>Conversation Models</Select.Label>
                            {conversationModelOptions.map((option, index) => (
                              <Select.Item index={index} key={option.value} value={option.value}>
                                <Select.ItemText>{option.label}</Select.ItemText>
                                <Select.ItemIndicator marginLeft="auto">
                                  <MaterialIcons name="check" size={16} />
                                </Select.ItemIndicator>
                              </Select.Item>
                            ))}
                          </Select.Group>
                        </Select.Viewport>

                        <Select.ScrollDownButton
                          alignItems="center"
                          justifyContent="center"
                          position="relative"
                          width="100%"
                          height="$3"
                        >
                          <YStack zIndex={10}>
                            <MaterialIcons name="keyboard-arrow-down" size={20} />
                          </YStack>
                        </Select.ScrollDownButton>
                      </Select.Content>
                    </Select>
                    {isUpdatingModel && (
                      <LottieView
                        source={require("@assets/animations/leaf_Loader.json")}
                        autoPlay
                        loop
                        style={{ width: 24, height: 24 }}
                      />
                    )}
                  </XStack>
                </YStack>

                {/* WhatsApp Phone Numbers Section */}
                <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginVertical="$4" />

                <YStack gap="$3">
                  <SizableText
                    size="$5"
                    color={theme ? "#1e293b" : "#f1f5f9"}
                    fontWeight="600"
                  >
                    WhatsApp Phone Numbers
                  </SizableText>
                  <SizableText
                    size="$3"
                    color={theme ? "#64748b" : "#94a3b8"}
                  >
                    {data?.phoneNumbers?.length || 0} phone numbers configured
                  </SizableText>

                  {data?.phoneNumbers?.length > 0 ? (
                    <YStack gap="$2">
                      {data.phoneNumbers.map((item: any, index: number) => (
                        <Card
                          key={index}
                          backgroundColor={theme ? "#f8fafc" : "#334155"}
                          borderRadius={12}
                          padding="$3"
                        >
                          <XStack alignItems="center" gap="$3">
                            <View style={{
                              backgroundColor: item.code_verification_status.includes("VERIFIED")
                                ? "#10b981"
                                : "#f59e0b",
                              borderRadius: 8,
                              padding: 8
                            }}>
                              <MaterialIcons
                                name={item.code_verification_status.includes("VERIFIED") ? "check-circle" : "pending"}
                                size={20}
                                color="white"
                              />
                            </View>
                            <YStack flex={1}>
                              <SizableText
                                size="$4"
                                color={theme ? "#1e293b" : "#f1f5f9"}
                                fontWeight="600"
                              >
                                {item.display_phone_number}
                              </SizableText>
                              <SizableText
                                size="$2"
                                color={theme ? "#64748b" : "#94a3b8"}
                              >
                                {item.code_verification_status}
                              </SizableText>
                            </YStack>
                          </XStack>
                        </Card>
                      ))}
                    </YStack>
                  ) : (
                    <Card
                      backgroundColor={theme ? "#fef3c7" : "#78350f"}
                      borderRadius={12}
                      padding="$4"
                    >
                      <YStack alignItems="center" gap="$3">
                        <MaterialIcons name="phone-disabled" size={32} color={theme ? "#d97706" : "#fbbf24"} />
                        <SizableText
                          size="$3"
                          color={theme ? "#d97706" : "#fbbf24"}
                          textAlign="center"
                        >
                          No phone numbers configured yet
                        </SizableText>
                      </YStack>
                    </Card>
                  )}
                </YStack>

                {/* Test WhatsApp Section */}
                <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginVertical="$4" />

                <Button
                  backgroundColor="#25D366"
                  color="white"
                  onPress={handleTest}
                  borderRadius={12}
                  paddingVertical="$3"
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialCommunityIcons name="whatsapp" size={20} color="white" />
                    <SizableText color="white" fontWeight="600" size="$4">
                      Send Test Message
                    </SizableText>
                  </XStack>
                </Button>
              </Card>
            </Animatable.View>

            {/* Success Message */}
            <Animatable.View animation="fadeInUp" duration={600} delay={200}>
              <Card
                backgroundColor={theme ? "#f0fdf4" : "#064e3b"}
                borderColor={theme ? "#dcfce7" : "#065f46"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                <YStack alignItems="center" gap="$3">
                  <View style={{
                    backgroundColor: theme ? "#d1fae5" : "#065f46",
                    borderRadius: 50,
                    padding: 12
                  }}>
                    <MaterialIcons name="check-circle" size={32} color={theme ? "#10b981" : "#34d399"} />
                  </View>
                  <H4
                    color={theme ? "#059669" : "#10b981"}
                    fontWeight="600"
                    textAlign="center"
                  >
                    WhatsApp Business Connected!
                  </H4>
                  <SizableText
                    size="$4"
                    color={theme ? "#065f46" : "#34d399"}
                    textAlign="center"
                  >
                    Your WhatsApp Business account is fully integrated. You can now manage messages, templates, and phone numbers directly from SmartSaaS.
                  </SizableText>
                </YStack>
              </Card>
            </Animatable.View>
          </>
        )}

        {/* Test Message Modal */}
        <Modal
          visible={showTestModal}
          transparent
          animationType="slide"
          onRequestClose={() => setShowTestModal(false)}
        >
          <View style={{
            flex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 20
          }}>
            <Card
              backgroundColor={theme ? "#ffffff" : "#1e293b"}
              borderRadius={16}
              padding="$5"
              maxWidth={600}
              width="100%"
              maxHeight="90%"
            >
              <ScrollView showsVerticalScrollIndicator={false}>
                <YStack gap="$4">
                  <XStack justifyContent="space-between" alignItems="center">
                    <H3 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="700">
                      Send Test Message
                    </H3>
                    <Pressable onPress={() => setShowTestModal(false)}>
                      <MaterialIcons name="close" size={24} color={theme ? "#1e293b" : "#f1f5f9"} />
                    </Pressable>
                  </XStack>

                  <Separator borderColor={theme ? "#e2e8f0" : "#334155"} />

                  {/* Phone Number Input */}
                  <YStack gap="$2">
                    <SizableText size="$4" color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">
                      Recipient Phone Number
                    </SizableText>
                    <Input
                      value={testPhoneNumber}
                      onChangeText={setTestPhoneNumber}
                      placeholder="+1234567890"
                      backgroundColor={theme ? "#f8fafc" : "#334155"}
                      borderColor={theme ? "#cbd5e1" : "#475569"}
                      color={theme ? "#1e293b" : "#f1f5f9"}
                      placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                    />
                  </YStack>

                  {/* Template Selection */}
                  <YStack gap="$2">
                    <SizableText size="$4" color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">
                      Select Template (Optional)
                    </SizableText>
                    <SizableText size="$3" color={theme ? "#64748b" : "#94a3b8"}>
                      Leave empty to send a simple test message
                    </SizableText>
                    <Select
                      value={selectedTemplate?.name || ''}
                      onValueChange={(value) => {
                        const template = approvedTemplates.find((t: any) => t.name === value);
                        if (template) {
                          handleTemplateSelect(template);
                        }
                      }}
                    >
                      <Select.Trigger
                        backgroundColor={theme ? "#f8fafc" : "#334155"}
                        borderColor={theme ? "#cbd5e1" : "#475569"}
                        iconAfter={<MaterialIcons name="keyboard-arrow-down" size={20} />}
                      >
                        <Select.Value placeholder="Select a template..." />
                      </Select.Trigger>
                      <Adapt>
                        <Sheet modal dismissOnSnapToBottom>
                          <Sheet.Frame>
                            <Sheet.ScrollView>
                              <Adapt.Contents />
                            </Sheet.ScrollView>
                          </Sheet.Frame>
                          <Sheet.Overlay
                            animation="lazy"
                            enterStyle={{ opacity: 0 }}
                            exitStyle={{ opacity: 0 }}
                          />
                        </Sheet>
                      </Adapt>
                      <Select.Content zIndex={200000}>
                        <Select.ScrollUpButton />
                        <Select.Viewport minHeight={200}>
                          <Select.Group>
                            <Select.Label>Templates</Select.Label>
                            <Select.Item index={0} value="" key="none">
                              <Select.ItemText>No Template (Simple Message)</Select.ItemText>
                            </Select.Item>
                            {approvedTemplates.map((template: any, index: number) => (
                              <Select.Item index={index + 1} value={template.name} key={template.name}>
                                <Select.ItemText>{template.name} ({template.language})</Select.ItemText>
                              </Select.Item>
                            ))}
                          </Select.Group>
                        </Select.Viewport>
                        <Select.ScrollDownButton />
                      </Select.Content>
                    </Select>
                  </YStack>

                  {/* Template Preview and Placeholders */}
                  {selectedTemplate && (
                    <>
                      <Separator borderColor={theme ? "#e2e8f0" : "#334155"} />
                      
                      <YStack gap="$3">
                        <SizableText size="$4" color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">
                          Template Preview
                        </SizableText>
                        <Card
                          backgroundColor={theme ? "#f8fafc" : "#334155"}
                          borderRadius={8}
                          padding="$3"
                        >
                          <YStack gap="$2">
                            {selectedTemplate.components?.map((component: any, idx: number) => (
                              <SizableText
                                key={idx}
                                size="$3"
                                color={theme ? "#64748b" : "#94a3b8"}
                                fontStyle="italic"
                              >
                                {component.type}: {component.text || component.format || 'N/A'}
                              </SizableText>
                            ))}
                          </YStack>
                        </Card>
                      </YStack>

                      {/* Placeholder Inputs */}
                      {extractPlaceholders(selectedTemplate).length > 0 && (
                        <YStack gap="$3">
                          <SizableText size="$4" color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">
                            Fill Template Placeholders
                          </SizableText>
                          {extractPlaceholders(selectedTemplate).map((placeholder) => (
                            <YStack key={placeholder.name} gap="$2">
                              <SizableText size="$3" color={theme ? "#64748b" : "#94a3b8"}>
                                {placeholder.name} ({placeholder.componentType})
                              </SizableText>
                              <Input
                                value={placeholderValues[placeholder.name] || ''}
                                onChangeText={(text) => {
                                  setPlaceholderValues(prev => ({
                                    ...prev,
                                    [placeholder.name]: text
                                  }));
                                }}
                                placeholder={`Enter value for ${placeholder.name}`}
                                backgroundColor={theme ? "#f8fafc" : "#334155"}
                                borderColor={theme ? "#cbd5e1" : "#475569"}
                                color={theme ? "#1e293b" : "#f1f5f9"}
                                placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                              />
                            </YStack>
                          ))}
                        </YStack>
                      )}
                    </>
                  )}

                  <Separator borderColor={theme ? "#e2e8f0" : "#334155"} />

                  {/* Action Buttons */}
                  <XStack gap="$3" justifyContent="flex-end">
                    <Button
                      backgroundColor={theme ? "#e2e8f0" : "#334155"}
                      color={theme ? "#1e293b" : "#f1f5f9"}
                      onPress={() => setShowTestModal(false)}
                      borderRadius={8}
                      paddingHorizontal="$4"
                      disabled={isSendingTest}
                    >
                      <SizableText fontWeight="600">Cancel</SizableText>
                    </Button>
                    <Button
                      backgroundColor="#25D366"
                      color="white"
                      onPress={handleSendTestMessage}
                      borderRadius={8}
                      paddingHorizontal="$4"
                      disabled={isSendingTest || !testPhoneNumber}
                    >
                      <XStack alignItems="center" gap="$2">
                        {isSendingTest ? (
                          <Spinner size="small" color="white" />
                        ) : (
                          <MaterialCommunityIcons name="whatsapp" size={20} color="white" />
                        )}
                        <SizableText color="white" fontWeight="600">
                          {isSendingTest ? 'Sending...' : 'Send Test'}
                        </SizableText>
                      </XStack>
                    </Button>
                  </XStack>
                </YStack>
              </ScrollView>
            </Card>
          </View>
        </Modal>
      </YStack>
    </ErrorBoundary>
  );
};

const StatBadge = ({ label, value, color, theme }: { label: string; value: number; color: string; theme: boolean }) => (
  <Card
    backgroundColor={theme ? "#f8fafc" : "#334155"}
    borderRadius={8}
    padding="$3"
    flex={1}
    minWidth={70}
  >
    <YStack alignItems="center" gap="$1">
      <SizableText
        fontSize="$6"
        fontWeight="700"
        color={color}
      >
        {value}
      </SizableText>
      <SizableText
        fontSize="$2"
        color={theme ? "#64748b" : "#94a3b8"}
        textAlign="center"
      >
        {label}
      </SizableText>
    </YStack>
  </Card>
);

export default WhatsappOptions;